/* eslint-disable @angular-eslint/prefer-inject */
import { InjectionToken, Signal, signal, WritableSignal } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, firstValueFrom } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { environment } from 'src/environments/environment';
import { DatabaseOperations } from '../constants';
import { WebsocketEventsEnum, WebsocketMessagesEnum } from '../enums';
import { GatewayResponseModel, WaytrBaseIdModel, WaytrBaseTimestampModel } from '../models';
import { AuthenticationService } from './authentication.service';
import { UserAccountService } from './user-account.service';

export const APP_UPDATES_WEBSOCKET_SERVICE = new InjectionToken<string>('AppUpdatesWebsocketService');
export const CART_ITEMS_WEBSOCKET_SERVICE = new InjectionToken<string>('CartItemsWebsocketService');
export const ORDERS_WEBSOCKET_SERVICE = new InjectionToken<string>('OrdersWebsocketService');
export const SUBORDERS_WEBSOCKET_SERVICE = new InjectionToken<string>('SubordersWebsocketService');
export const SESSIONS_WEBSOCKET_SERVICE = new InjectionToken<string>('SessionsWebsocketService');
export const NOTIFICATIONS_WEBSOCKET_SERVICE = new InjectionToken<string>('NotificationsWebsocketService');
export const WAITER_COMMANDS_WEBSOCKET_SERVICE = new InjectionToken<string>('WaiterCommandsWebsocketService');
export const WAITER_COMMANDS_VENUES_WEBSOCKET_SERVICE = new InjectionToken<string>('WaiterCommandsVenuesWebsocketService');
export const VENUE_WEBSOCKET_SERVICE = new InjectionToken<string>('VenueWebsocketService');
export const SUPPORT_CHAT_WEBSOCKET_SERVICE = new InjectionToken<string>('SupportChatWebsocketService');

export class WebsocketService<T extends WaytrBaseIdModel | WaytrBaseTimestampModel> {
  private readonly namespace: string;
  private readonly userAccountService: UserAccountService;
  private readonly authenticationService: AuthenticationService;
  private readonly router: Router;

  #websocket!: Socket;
  #isConnected = false;
  #reconnectTries = 0;
  #reconnectTimeout?: NodeJS.Timeout;
  #initialParams?: unknown;
  #eventListeners: Set<string> = new Set();
  #isRefreshingToken = false;
  #lastErrorLogTime = 0;
  #errorLogThrottleMs = 30000; // Only log errors once every 30 seconds
  #consecutiveConnectionFailures = 0;
  #maxQuietReconnectAttempts = 10; // After this many failures, stop logging errors

  readonly #options = {
    autoConnect: false, // Changed to false - we'll connect manually when JWT is ready
    reconnection: true,
    reconnectionAttempts: 15,
    reconnectionDelay: 2000,
    reconnectionDelayMax: 30000, // Max 30 seconds between attempts
    timeout: 20000,
    forceNew: true,
  };

  // Sources
  readonly #initialMessagesSource: WritableSignal<T[]> = signal([]);
  readonly #updatesSource: WritableSignal<T[]> = signal([]);
  readonly #socketIdSource: WritableSignal<string | undefined> = signal(undefined);
  readonly #connectionStatusSource: WritableSignal<boolean> = signal(false);

  // Exposed signals (read-only)
  readonly initialMessages: Signal<T[]> = this.#initialMessagesSource.asReadonly();
  readonly updates: Signal<T[]> = this.#updatesSource.asReadonly();
  readonly socketId: Signal<string | undefined> = this.#socketIdSource.asReadonly();
  readonly isConnected: Signal<boolean> = this.#connectionStatusSource.asReadonly();

  constructor(namespace: string, userAccountService: UserAccountService, authenticationService: AuthenticationService, router: Router) {
    this.namespace = namespace;
    this.userAccountService = userAccountService;
    this.authenticationService = authenticationService;
    this.router = router;
    this.createWebsocket();

    // Connect after JWT is available and watch for JWT updates
    this.connectWhenReady();
    this.watchForJWTUpdates();
  }

  private watchForJWTUpdates() {
    // Watch for JWT changes and reconnect with new token
    let previousToken: string | undefined;

    const checkJWT = () => {
      const currentToken = this.userAccountService.userJWT()?.accessToken;

      // If we got a new JWT (wasn't there before or changed)
      if (currentToken && currentToken !== previousToken) {
        previousToken = currentToken;

        // If already connected, reconnect with new JWT
        if (this.#websocket && this.#isConnected) {
          if (!environment.production) {
            console.log(`[WebSocket] JWT updated, reconnecting ${this.namespace} with new token`);
          }
          this.reconnectWithJWT();
        } else if (this.#websocket && !this.#isConnected) {
          // If not connected yet, connect with the JWT
          if (!environment.production) {
            console.log(`[WebSocket] JWT available, connecting ${this.namespace}`);
          }
          this.#websocket.auth = { token: currentToken };
          if (this.#websocket.io.opts.extraHeaders) {
            this.#websocket.io.opts.extraHeaders.Authorization = `Bearer ${currentToken}`;
          }
          this.#websocket.connect();
        }
      }

      // Keep checking periodically
      setTimeout(checkJWT, 200);
    };

    setTimeout(checkJWT, 100);
  }

  private reconnectWithJWT() {
    const accessToken = this.userAccountService.userJWT()?.accessToken;
    if (!accessToken) return;

    // Update auth
    if (this.#websocket) {
      this.#websocket.auth = { token: accessToken };
      if (this.#websocket.io.opts.extraHeaders) {
        this.#websocket.io.opts.extraHeaders.Authorization = `Bearer ${accessToken}`;
      }

      // Disconnect and reconnect to apply new JWT
      this.#websocket.disconnect();
      this.#websocket.connect();
    }
  }

  private connectWhenReady() {
    // Initial connection attempt - will be managed by watchForJWTUpdates
    setTimeout(() => {
      const accessToken = this.userAccountService.userJWT()?.accessToken;
      if (accessToken) {
        if (!environment.production) {
          console.log(`[WebSocket] Initial JWT available, connecting to ${this.namespace}`);
        }
        this.#websocket.connect();
      }
      // If no JWT, watchForJWTUpdates will handle connection when it becomes available
    }, 150);
  }

  private createWebsocket() {
    // Clean up existing socket first
    if (this.#websocket) {
      this.cleanupSocket();
    }

    // Get JWT token for authentication
    const accessToken = this.userAccountService.userJWT()?.accessToken;

    if (!accessToken && !environment.production) {
      console.warn(`[WebSocket] No access token available when connecting to ${this.namespace}`);
    }

    this.#websocket = io(`${environment.WS_URL}/${this.namespace}`, {
      ...this.#options,
      auth: {
        token: accessToken || '',
      },
      extraHeaders: accessToken
        ? {
            Authorization: `Bearer ${accessToken}`,
          }
        : {},
    });
    this.setupEventListeners();
  }

  private setupEventListeners() {
    // Clear previous listeners tracking
    this.#eventListeners.clear();

    // Setup connection events
    this.#websocket.on(WebsocketEventsEnum.CONNECT, () => this.handleConnection());
    this.#eventListeners.add(WebsocketEventsEnum.CONNECT);

    this.#websocket.once(WebsocketEventsEnum.DISCONNECT, reason => this.onDisconnect(reason));
    this.#eventListeners.add(WebsocketEventsEnum.DISCONNECT);

    this.#websocket.on(WebsocketMessagesEnum.INIT, message => this.handleInitialState(message));
    this.#eventListeners.add(WebsocketMessagesEnum.INIT);

    this.#websocket.on(WebsocketMessagesEnum.DATA, message => this.handleNewState(message));
    this.#eventListeners.add(WebsocketMessagesEnum.DATA);

    this.#websocket.io.on(WebsocketEventsEnum.ERROR, error => this.onError(error));
    this.#eventListeners.add(WebsocketEventsEnum.ERROR);

    this.#websocket.io.on(WebsocketEventsEnum.RECONNECT, attempt => this.onReconnect(attempt));
    this.#eventListeners.add(WebsocketEventsEnum.RECONNECT);

    this.#websocket.on(WebsocketEventsEnum.CONNECT_ERROR, error => this.onConnectionError(error));
    this.#eventListeners.add(WebsocketEventsEnum.CONNECT_ERROR);

    // Handle authentication errors specifically
    this.#websocket.io.on(WebsocketEventsEnum.ERROR, error => this.handleAuthError(error));
  }

  private cleanupSocket() {
    if (!this.#websocket) return;

    // Remove all tracked event listeners
    this.#eventListeners.forEach(event => {
      this.#websocket.off(event);
      // Only remove from io if it's a manager-level event
      if (event === WebsocketEventsEnum.ERROR || event === WebsocketEventsEnum.RECONNECT) {
        this.#websocket.io.off(event);
      }
    });
    this.#eventListeners.clear();

    // Remove all listeners as fallback
    this.#websocket.removeAllListeners();
    this.#websocket.io.removeAllListeners();

    // Disconnect and close
    if (this.#websocket.connected) {
      this.#websocket.disconnect();
    }
    this.#websocket.close();

    // Clear connection state
    this.#isConnected = false;
    this.#connectionStatusSource.set(false);
    this.#socketIdSource.set(undefined);
  }

  public sendMessage(eventName: string, message?: unknown) {
    if (this.#websocket && this.#isConnected) {
      this.#websocket.emit(eventName, message);
    } else if (!environment.production) {
      console.warn(`Cannot send message ${eventName}: WebSocket not connected`);
    }
  }

  public sendInitialParams(message: unknown) {
    this.sendMessage(WebsocketMessagesEnum.PARAMS, message);
    this.#initialParams = message;
  }

  public closeWebSocket() {
    // Clear reconnection timeout
    if (this.#reconnectTimeout) {
      clearTimeout(this.#reconnectTimeout);
      this.#reconnectTimeout = undefined;
    }

    // Clean up socket
    this.cleanupSocket();

    // Reset all state
    this.resetMessages();
    this.#reconnectTries = 0;
  }

  public resetMessages() {
    this.#initialMessagesSource.set([]);
    this.#updatesSource.set([]);
  }

  public resetConsumedUpdates() {
    this.#updatesSource.set([]);
  }

  public forceReconnect() {
    if (!environment.production) {
      console.log(`[WebSocket] Force reconnecting to ${this.namespace}`);
    }
    this.closeWebSocket();
    this.createWebsocket();
  }

  private handleConnection() {
    this.#socketIdSource.set(this.#websocket.id);
    this.#isConnected = true;
    this.#connectionStatusSource.set(true);
    this.#reconnectTries = 0;
    this.#consecutiveConnectionFailures = 0; // Reset failure counter on successful connection

    if (!environment.production) {
      console.log(`[WebSocket] ✅ Connected to ${this.namespace} with ID: ${this.#websocket.id}`);
    }

    // Resend initial params if available
    if (this.#initialParams) {
      this.sendInitialParams(this.#initialParams);
    }
  }

  private handleInitialState(data: T[]) {
    // Ensure data is valid array before setting
    if (Array.isArray(data)) {
      this.#initialMessagesSource.set(data);
    } else {
      if (!environment.production) {
        console.warn('[WebSocket] Invalid initial state data received:', data);
      }
      this.#initialMessagesSource.set([]);
    }
  }

  private handleNewState(data: GatewayResponseModel<T>) {
    if (!data?.document) {
      if (!environment.production) {
        console.warn('[WebSocket] Invalid update data received:', data);
      }
      return;
    }

    this.#initialMessagesSource.update(values => this.handleUpdate(data, values));
    this.#updatesSource.update(values => [...values, data.document]);
  }

  private handleUpdate(data: GatewayResponseModel<T>, values: T[]) {
    if (!data?.document) return values;

    switch (data.operation) {
      case DatabaseOperations.INSERT:
        return [...values, data.document];
      case DatabaseOperations.DELETE:
        return values.filter(val => val._id !== data.document?._id);
      case DatabaseOperations.UPDATE:
        return values.map(val => (val._id === data.document?._id ? data.document : val));
      default:
        if (!environment.production) {
          console.warn('[WebSocket] Unknown operation:', data.operation);
        }
        return values;
    }
  }

  private onDisconnect(reason: Socket.DisconnectReason) {
    // Only log if we should (throttle errors when server is persistently down)
    if (!environment.production && this.shouldLogError()) {
      console.warn(`[WebSocket] ⚠️ Disconnected from ${this.namespace}. Reason:`, reason);
    }

    this.#isConnected = false;
    this.#connectionStatusSource.set(false);
    this.#socketIdSource.set(undefined);

    // Handle different disconnect reasons
    if (reason === 'io server disconnect') {
      // Server initiated disconnect - reconnect manually
      this.attemptReconnection();
    } else if (reason === 'transport error' || reason === 'transport close') {
      // Network issues - attempt reconnection with backoff
      this.attemptReconnection();
    }
    // For other reasons, let Socket.IO handle automatic reconnection
  }

  private attemptReconnection() {
    this.#reconnectTries++;
    this.#consecutiveConnectionFailures++;

    // Clear existing timeout
    if (this.#reconnectTimeout) {
      clearTimeout(this.#reconnectTimeout);
    }

    // Exponential backoff with max delay of 30 seconds
    const delay = Math.min(2000 * Math.pow(1.5, this.#reconnectTries), 30000);

    // Only log if we should (reduce console spam)
    if (!environment.production && this.shouldLogError()) {
      console.log(`[WebSocket] 🔄 Reconnecting to ${this.namespace} in ${Math.round(delay / 1000)}s... (attempt ${this.#reconnectTries})`);
    }

    this.#reconnectTimeout = setTimeout(() => this.forceReconnect(), delay);
  }

  private onError(error: Error) {
    this.#consecutiveConnectionFailures++;

    // Only log if we should (throttle error logging)
    if (!environment.production && this.shouldLogError()) {
      console.error(`[WebSocket] ❌ Connection error on ${this.namespace}:`, error);
    }
  }

  private onReconnect(attempt: number) {
    if (!environment.production) {
      console.log(`[WebSocket] Reconnected to ${this.namespace} on attempt ${attempt}`);
    }
    this.#reconnectTries = 0;
  }

  private onConnectionError(error?: Error) {
    this.#consecutiveConnectionFailures++;

    // Only log if we should (throttle error logging)
    if (!environment.production && this.shouldLogError()) {
      console.error(`[WebSocket] ❌ Connection failed for ${this.namespace}:`, error?.message || 'Server unavailable');
    }

    // Only check for authentication errors if it's not a generic connection failure
    // When server is down, we get "xhr poll error" which is not an auth issue
    if (error?.message && !this.isServerDownError(error.message)) {
      if (error.message.includes('Unauthorized') || error.message.includes('jwt') || error.message.includes('401')) {
        this.handleAuthError(error);
      }
    }
  }

  private async handleAuthError(error: Error) {
    // Prevent multiple simultaneous token refresh attempts
    if (this.#isRefreshingToken) {
      return;
    }

    // Don't try to refresh token if server is down (will just generate more errors)
    if (this.isServerDownError(error.message)) {
      return;
    }

    if (!environment.production && this.shouldLogError()) {
      console.warn(`[WebSocket] 🔐 Authentication error on ${this.namespace}:`, error.message);
    }

    const refreshToken = this.userAccountService.userJWT()?.refreshToken;

    // If no refresh token available, logout immediately
    if (!refreshToken) {
      if (!environment.production) {
        console.error(`[WebSocket] No refresh token available, logging out`);
      }
      this.authenticationService.logout();
      return;
    }

    this.#isRefreshingToken = true;

    try {
      // Attempt to refresh the token
      // Use catchError to prevent EmptyError if the observable completes without emitting
      const newTokens = await firstValueFrom(
        this.authenticationService.refreshToken({ refreshToken }).pipe(
          catchError(error => {
            // Re-throw the error to be caught by the outer catch block
            throw error;
          }),
        ),
      );

      // Update user account with new tokens
      this.userAccountService.setUserAccount(newTokens);

      if (!environment.production) {
        console.log(`[WebSocket] ✅ Token refreshed successfully for ${this.namespace}, reconnecting...`);
      }

      // Update socket auth with new token
      if (this.#websocket) {
        this.#websocket.auth = { token: newTokens.accessToken };
        // Also update extra headers for authorization
        if (this.#websocket.io.opts.extraHeaders) {
          this.#websocket.io.opts.extraHeaders.Authorization = `Bearer ${newTokens.accessToken}`;
        }
        this.#websocket.connect();
      }

      this.#isRefreshingToken = false;
    } catch (refreshError) {
      this.#isRefreshingToken = false;

      // Only log if we should (throttle error logging)
      if (!environment.production && this.shouldLogError()) {
        console.error(`[WebSocket] ❌ Token refresh failed for ${this.namespace}:`, refreshError);
      }

      // Only logout on authentication failures (401, 403, 404), not on server errors (0, 500, 502, 503, etc.)
      // Server errors are transient and may occur during backend restart
      if (refreshError instanceof Object && 'status' in refreshError) {
        const errorStatus = (refreshError as { status: number }).status;
        if (errorStatus === 401 || errorStatus === 404 || errorStatus === 403) {
          // Genuine authentication failure - logout
          this.authenticationService.logout();
        } else if (!environment.production && this.shouldLogError()) {
          console.warn(`[WebSocket] ⚠️ Not logging out due to server error (${errorStatus}), will retry on next connection`);
        }
      }
      // For network errors (status 0) or unknown errors, don't logout - server might be down
    }
  }

  /**
   * Determines if we should log an error based on throttling rules
   * Reduces console spam when server is persistently down
   */
  private shouldLogError(): boolean {
    const now = Date.now();

    // After many consecutive failures, only log occasionally
    if (this.#consecutiveConnectionFailures > this.#maxQuietReconnectAttempts) {
      // Only log once every 30 seconds after many failures
      if (now - this.#lastErrorLogTime < this.#errorLogThrottleMs) {
        return false;
      }
    }

    this.#lastErrorLogTime = now;
    return true;
  }

  /**
   * Checks if error message indicates server is down (not an auth issue)
   */
  private isServerDownError(message: string): boolean {
    const serverDownIndicators = [
      'xhr poll error',
      'websocket error',
      'transport error',
      'connection refused',
      'ECONNREFUSED',
      'network error',
      'fetch failed',
      'ERR_CONNECTION_REFUSED',
    ];

    return serverDownIndicators.some(indicator => message.toLowerCase().includes(indicator.toLowerCase()));
  }
}
